// PlaygroundPage - Blop compiler running in the browser

import { compile } from '../../src/compile-browser.js'
import { highlightCodeForEditor } from '../highlight.js'
import { grammar } from '../../src/grammar.js'
import { tokensDefinition } from '../../src/tokensDefinition.js'
import 'blop' as blopRuntime

def PlaygroundPage(ctx: Component): VNode {
  defaultCode = 'def Counter(ctx: Component): VNode {\n  { value as count, setState } = ctx.state<number>(\'count\', 0)\n\n  color = \'#6c757d\'\n  message = \'Start counting!\'\n  if count > 0 {\n    color := \'#198754\'\n    message := \'Positive\'\n  } elseif count < 0 {\n    color := \'#dc3545\'\n    message := \'Negative\'\n  }\n\n  <div style={ textAlign: \'center\', padding: \'2em\', fontFamily: \'system-ui\' }>\n    <div style={ fontSize: \'4em\', fontWeight: \'bold\', color, lineHeight: \'1\',\n        marginBottom: \'0.25em\' }>\n      count\n    </div>\n    <div style={ color, fontSize: \'0.9em\', marginBottom: \'1.5em\' }>\n      = message\n    </div>\n    <div style={ display: \'flex\', gap: \'0.5em\', justifyContent: \'center\' }>\n      <button class="btn btn-outline-danger"\n        on={ click: () => setState(count - 1) }>\n        \'−\'\n      </button>\n      <button class="btn btn-outline-secondary"\n        on={ click: () => setState(0) }>\n        \'Reset\'\n      </button>\n      <button class="btn btn-outline-success"\n        on={ click: () => setState(count + 1) }>\n        \'+\'\n      </button>\n    </div>\n  </div>\n}\n\ndef main(): VNode {\n  <Counter />\n}'

  { value as code, setState as setCode } = ctx.state<string>('code', defaultCode)
  { value as output, setState as setOutput } = ctx.state<string>('output', '')
  { value as error, setState as setError } = ctx.state<string | null>('error', null)
  { value as warning, setState as setWarning } = ctx.state<string | null>('warning', null)
  { value as isCompiling, setState as setIsCompiling } = ctx.state<boolean>('isCompiling', false)
  { value as activeTab, setState as setActiveTab } = ctx.state<string>('activeTab', 'preview')
  { value as previewFn, setState as setPreviewFn } = ctx.state<any>('previewFn', null)
  { value as previewKey, setState as setPreviewKey } = ctx.state<number>('previewKey', 0)
  { value as previewError, setState as setPreviewError } = ctx.state<string | null>('previewError', null)
  { value as highlightedInput, setState as setHighlightedInput } = ctx.state<string>('highlightedInput', '')



  def executePreview(compiledCode, exportKeys) {
    if !compiledCode || !exportKeys || exportKeys.length == 0 {
      return null
    }
    entryKey = exportKeys.find((k) => k == 'main')
    if !entryKey {
      return null
    }
    exportIdx = compiledCode.lastIndexOf('\nexport {')
    cutAt = exportIdx
    if exportIdx < 0 {
      cutAt := compiledCode.length
    }
    execCode = compiledCode.substring(0, cutAt) + '\nreturn { ' + exportKeys.join(', ') + ' };'
    try {
      fn = new Function('blop', execCode)
      result = fn(blopRuntime)
      return result[entryKey]
    } catch e {
      console.log('Error executing preview code', e)
      return null
    }
  }

  def applyHighlight(vnode) {
    vnode.elm.innerHTML = highlightedInput
  }

  def syncScroll(textarea) {
    preEl = if textarea.previousElementSibling => textarea.previousElementSibling.querySelector('pre') else null
    if preEl {
      preEl.scrollTop = textarea.scrollTop
      preEl.scrollLeft = textarea.scrollLeft
    }
  }

  def handleScroll(e) {
    syncScroll(e.target)
  }

  editorHeight = '520px'

  def compileCode(sourceCode) {
    src = if sourceCode => sourceCode else code
    setIsCompiling(true)
    setError(null)
    setOutput('')

    result = compile(src, { inference: true })
    if result.success {
      setOutput(result.code)
      setError(null)
      setWarning(null)
      result.warnings.forEach((_warning) => {
        setWarning(_warning.message)
      })
      previewHtml = executePreview(result.code, result.exportKeys)
      setPreviewFn(previewHtml)
      setPreviewKey(previewKey + 1)
      setPreviewError(null)
    } else {
      errorMsg = result.errors[0].message || 'Compilation failed'
      setError(errorMsg)
      setOutput('')
      setPreviewFn(null)
    }
    setIsCompiling(false)
  }

  def handleCodeChange(e) {
    newCode = e.target.value
    setCode(newCode)
    syncScroll(e.target)
    highlightCodeForEditor(newCode, 'blop').then((html) => {
      setHighlightedInput(html)
    })
    clearTimeout(ctx.state._debounceTimer)
    ctx.state._debounceTimer = setTimeout(() => {
      compileCode(newCode)
    }, 300)
  }

  def loadExample(exampleCode) {
    return () => {
      setCode(exampleCode)
      setOutput('')
      setError(null)
      setWarning(null)
      setPreviewFn(null)
      highlightCodeForEditor(exampleCode, 'blop').then((html) => {
        setHighlightedInput(html)
      })
      compileCode(exampleCode)
    }
  }

  ctx.mount(async () => {
    html = await highlightCodeForEditor(code, 'blop')
    setHighlightedInput(html)
    compileCode(code)
  })

  <div style={ padding: '0 1.5em 2em' }>
    // Header
    <div style={
        borderBottom: '1px solid #dee2e6',
        padding: '2em 0 1.75em',
        marginBottom: '1.5em'
    }>
      <h1 style={ fontWeight: '700', marginBottom: '0.35em', fontSize: '2em' }>
        'Playground'
      </h1>
      <p style={ color: '#495057', fontSize: '1.05em', marginBottom: '0', lineHeight: '1.5' }>
        'Write Blop code and see the compiled JavaScript output. The compiler runs entirely in the browser.'
      </p>
    </div>

    // Editor row
    <div class="row">
      // Input
      <div class="col-lg-6" style={ marginBottom: '1.5em' }>
        <div class="card h-100">
          <div class="card-header" style={ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: '0.85em', color: '#6c757d' }>
            <span>'Input — Blop'</span>
          </div>
          <div class="card-body" style={ padding: '0', position: 'relative', height: editorHeight, overflow: 'hidden',
              backgroundColor: '#1a202c' }>
            <div
              hooks={ insert: applyHighlight, update: applyHighlight }
              style={ position: 'absolute', top: '0', left: '0', right: '0', bottom: '0',
                overflow: 'hidden', pointerEvents: 'none', zIndex: '1' } />
            <textarea
              style={
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                height: editorHeight,
                width: '100%',
                border: 'none',
                fontFamily: '\'SFMono-Regular\', Consolas, \'Liberation Mono\', Menlo, monospace',
                fontSize: '13px',
                resize: 'none',
                backgroundColor: 'transparent',
                color: 'transparent',
                caretColor: '#e2e8f0',
                padding: '1.25em 1.5em',
                outline: 'none',
                lineHeight: '1.65',
                zIndex: '2'
            }
              value=code
              on={ input: handleCodeChange, scroll: handleScroll }
              spellcheck="false"
              placeholder="Write your Blop code here..." />
          </div>
          <div class="card-footer" style={ padding: '0.75em 1em', display: 'flex', alignItems: 'center', gap: '1em' }>
            if isCompiling {
              <button class="btn btn-sm btn-primary" disabled=true>
                <span class="spinner-border spinner-border-sm" style={ marginRight: '0.4em' }></span>
                'Compiling...'
              </button>
            } else {
              <button class="btn btn-sm btn-primary" on={ click: () => compileCode(null) }>
                'Compile'
              </button>
            }
            <span style={ fontSize: '0.85em', color: '#6c757d' }>'Ctrl+Enter also works in most browsers'</span>
          </div>
        </div>
      </div>

      // Output
      <div class="col-lg-6" style={ marginBottom: '1.5em' }>
        <div class="card h-100">
          <div class="card-header" style={ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: '0.85em', color: '#6c757d' }>
            <div style={ display: 'flex', gap: '0.4em' }>
              <button
                class={ 'btn': true, 'btn-sm': true, 'btn-secondary': activeTab == 'js',
                  'btn-outline-secondary': activeTab != 'js' }
                on={ click: () => setActiveTab('js') }>
                'JavaScript'
              </button>
              <button
                class={ 'btn': true, 'btn-sm': true, 'btn-secondary': activeTab == 'preview',
                  'btn-outline-secondary': activeTab != 'preview' }
                on={ click: () => setActiveTab('preview') }>
                'Preview'
              </button>
            </div>
            if output {
              <span style={ color: '#198754' }>'✓ Compiled'</span>
            } elseif error {
              <span style={ color: '#dc3545' }>'Compilation error'</span>
            }
          </div>
          <div class="card-body" style={ padding: '0' }>
            if warning {
              <div style={
                  padding: '1em 1.5em',
                  backgroundColor: '#fff8e1',
                  color: '#6d4c00',
                  fontFamily: '\'SFMono-Regular\', Consolas, \'Liberation Mono\', Menlo, monospace',
                  fontSize: '13px',
                  borderBottom: '1px solid #dee2e6',
                  whiteSpace: 'pre-wrap'
              }>
                <strong style={ display: 'block', marginBottom: '0.5em', fontFamily: 'inherit' }>'Warning'</strong>
                = warning
              </div>
            }

            if error {
              <pre style={
                  height: editorHeight,
                  margin: '0',
                  padding: '1.25em 1.5em',
                  backgroundColor: '#2d1a1a',
                  color: '#f8b4b4',
                  fontFamily: '\'SFMono-Regular\', Consolas, \'Liberation Mono\', Menlo, monospace',
                  fontSize: '13px',
                  overflowY: 'auto',
                  border: 'none',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.65'
              }><code>error</code></pre>
            }

            if activeTab == 'js' && !error {
              if output {
                <pre style={
                    height: editorHeight,
                    margin: '0',
                    padding: '1.25em 1.5em',
                    backgroundColor: '#1a202c',
                    color: '#e2e8f0',
                    fontFamily: '\'SFMono-Regular\', Consolas, \'Liberation Mono\', Menlo, monospace',
                    fontSize: '13px',
                    overflowY: 'auto',
                    border: 'none',
                    lineHeight: '1.65'
                }><code>output</code></pre>
              } else {
                <div style={
                    height: editorHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#adb5bd',
                    fontSize: '0.95em'
                }>
                  'Press Compile to see output'
                </div>
              }
            }
            // Preview tab — previewFn is rendered as a real component in the normal tree
            previewDisplay = 'none'
            if activeTab == 'preview' && !error {
              previewDisplay := 'block'
            }
            if previewError {
              <div style={
                  height: editorHeight,
                  padding: '1em 1.5em',
                  backgroundColor: '#fff8e1',
                  color: '#6d4c00',
                  fontFamily: '\'SFMono-Regular\', Consolas, \'Liberation Mono\', Menlo, monospace',
                  fontSize: '13px',
                  borderBottom: '1px solid #dee2e6',
                  whiteSpace: 'pre-wrap'
              }>
                <strong style={ display: 'block', marginBottom: '0.5em', fontFamily: 'inherit' }>'Preview error'</strong>
                = previewError
              </div>
            }
            <div style={ height: editorHeight, overflowY: 'auto', backgroundColor: '#ffffff', display: previewDisplay }>
              if previewFn {
                = blopRuntime.c(previewFn, { key: previewKey }, [], 'preview')
              } elseif !output {
                <div style={
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#adb5bd',
                    fontSize: '0.95em'
                }>
                  'Press Compile to see preview'
                </div>
              }
            </div>
          </div>
          <div class="card-footer" style={ padding: '0.75em 1em', fontSize: '0.85em', color: '#6c757d' }>
            'The compiler runs in-browser via compile-browser.js — no server required.'
          </div>
        </div>
      </div>
    </div>

    // Examples + reference row
    <div class="row">
      <div class="col-lg-8" style={ marginBottom: '1.5em' }>
        <div class="card">
          <div class="card-header" style={ fontSize: '0.85em', color: '#6c757d' }>'Load example'</div>
          <div class="card-body" style={ padding: '1em' }>
            <div style={ display: 'flex', gap: '0.5em', flexWrap: 'wrap' }>
              examples = [
              { name: 'Simple component',
                code: 'def Greeting({ attributes }: Component): VNode {\n  { name } = attributes\n\n  <div style={ padding: \'2em\' }>\n    <h1>\'Hello, \'name</h1>\n    <p>\'Welcome to Blop.\'</p>\n  </div>\n}\n\ndef main(): VNode {\n  <Greeting name=\'World\' />\n}'
              },
{ name: 'Counter with state',
                code: 'def Counter(ctx: Component): VNode {\n  { value as count, setState } = ctx.state<number>(\'count\', 0)\n\n  <div style={ textAlign: \'center\', padding: \'2em\' }>\n    <div style={ fontSize: \'3em\', fontWeight: \'bold\', marginBottom: \'1em\' }>count</div>\n    <div style={ display: \'flex\', gap: \'0.5em\', justifyContent: \'center\' }>\n      <button class="btn btn-primary"\n        on={ click: () => setState(count + 1) }>\n        \'Increment\'\n      </button>\n      <button class="btn btn-outline-secondary"\n        on={ click: () => setState(0) }>\n        \'Reset\'\n      </button>\n    </div>\n  </div>\n}\n\ndef main(): VNode {\n  <Counter />\n}'
              },
{ name: 'List with for loop',
                code: 'def ItemList({ attributes }: Component): VNode {\n  { items } = attributes\n\n  <ul class="list-group">\n    for index, item of items {\n      <li class="list-group-item">\n        <span style={ color: \'#6c757d\', fontSize: \'0.85em\', marginRight: \'0.5em\' }>\'#\'(index + 1)</span>\n        = item\n      </li>\n    }\n  </ul>\n}\n\ndef main(): VNode {\n  items = [\'Apple\', \'Banana\', \'Cherry\']\n  <ItemList items=items />\n}'
              },
{ name: 'Conditional rendering',
                code: 'def StatusBadge(ctx: Component): VNode {\n  { value as status, setState as setStatus } = ctx.state<string>(\'status\', \'online\')\n\n  statuses = [\'online\', \'degraded\', \'offline\']\n\n  <div style={ padding: \'1.5em\', fontFamily: \'system-ui\' }>\n    if status == \'online\' {\n      <p style={ color: \'#198754\' }>\'✓ All systems operational\'</p>\n    } elseif status == \'degraded\' {\n      <p style={ color: \'#fd7e14\' }>\'⚠ Partial outage\'</p>\n    } else {\n      <p style={ color: \'#dc3545\' }>\'✗ Major outage\'</p>\n    }\n    <div style={ display: \'flex\', gap: \'0.4em\', marginTop: \'1em\' }>\n      for s in statuses {\n        cls = if s == status => \'btn btn-sm btn-secondary\' else \'btn btn-sm btn-outline-secondary\'\n        <button class=cls on={ click: () => setStatus(s) }>s</button>\n      }\n    </div>\n  </div>\n}\n\ndef main(): VNode {\n  <StatusBadge />\n}'
              },
{ name: 'Type system',
                code: 'type Status = \'idle\' | \'loading\' | \'success\'\n\ntype User = { name: string, age: number, role: \'admin\' | \'viewer\' }\n\ndef formatUser(user: User): string {\n  label = user.name\' (age: \'user.age\')\'\n  return label\n}\n\ndef TypeShowcase(ctx: Component): VNode {\n  { value as status, setState as setStatus } = ctx.state<Status>(\'status\', \'idle\')\n  { value as user, setState as setUser } = ctx.state<User | null>(\'user\', null)\n\n  def loadUser() {\n    setStatus(\'loading\')\n    setUser({ name: \'Alice\', age: 30, role: \'admin\' })\n    setStatus(\'success\')\n  }\n\n  <div style={ padding: \'2em\' }>\n    <h2>\'Type system demo\'</h2>\n    <p>\'Status: \'status</p>\n    if user {\n      <p>formatUser(user)</p>\n      <p>\'Role: \'user.role</p>\n    }\n    <button class="btn btn-primary" on={ click: loadUser }>\'Load user\'</button>\n  </div>\n}\n\ndef main(): VNode {\n  <TypeShowcase />\n}'
              }
              ]

              for example in examples {
                <button
                  class="btn btn-sm btn-outline-secondary"
                  on={ click: loadExample(example.code) }>
                  = example.name
                </button>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4" style={ marginBottom: '1.5em' }>
        <div class="card">
          <div class="card-header" style={ fontSize: '0.85em', color: '#6c757d' }>'Reference'</div>
          <div class="card-body" style={ padding: '1em 1.25em', fontSize: '0.9em' }>
            <ul style={ margin: '0', paddingLeft: '1.25em', lineHeight: '2' }>
              <li>
                <a href="https://github.com/batiste/blop-language/blob/master/docs/SYNTAX_REFERENCE.md" target="_blank">
                  'Syntax reference'
                </a>
              </li>
              <li>
                <a href="https://github.com/batiste/blop-language/blob/master/docs/QUICK_START.md" target="_blank">
                  'Quick start guide'
                </a>
              </li>
              <li>
                <a href="https://github.com/batiste/blop-language" target="_blank">
                  'GitHub repository'
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
}
